(() => {
  if (customElements.get('maxler-quick-purchase')) return;

  class MaxlerQuickPurchase extends HTMLElement {
    static submitting = false;

    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.dialog = this.querySelector('[data-qp-dialog]');
      this.content = this.querySelector('[data-qp-content]');
      this.titleElement = this.querySelector('[data-qp-title]');
      this.details = this.querySelector('[data-qp-details]');
      if (!this.dialog || !this.content) return;
      const signal = this.events.signal;
      document.addEventListener('collection:reloaded', () => this.restoreCardFocus(true), {signal});
      this.addEventListener('click', event => {
        const close = event.target.closest('[data-qp-close]');
        if (close) {
          event.preventDefault();
          this.close();
          return;
        }
        const trigger = event.target.closest('[data-qp-open]');
        if (!trigger || !this.contains(trigger) || trigger.disabled) return;
        event.preventDefault();
        if (this.loading || MaxlerQuickPurchase.submitting || this.historyClosing) return;
        if (trigger.dataset.directVariant) this.directAdd(trigger);
        else this.open(trigger);
      }, {signal});
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, {signal});
      this.dialog.addEventListener('keydown', event => this.trapFocus(event), {signal});
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, {signal});
      window.addEventListener('popstate', () => {
        if (this.historyClosing) {
          this.historyClosing();
          this.historyClosing = null;
        }
        if (this.dialog.open && !this.ownsHistory()) this.close({consumeHistory: false});
      }, {signal});
    }

    disconnectedCallback() {
      this.fetchController?.abort();
      this.formEvents?.abort();
      this.close({consumeHistory: true, returnFocus: false});
      this.events?.abort();
      this.events = null;
    }

    text(name) {
      return this.dataset['qp' + name.charAt(0).toUpperCase() + name.slice(1)] || '';
    }

    setCardMessage(trigger, message, error = false) {
      const card = trigger.closest('.grid-product__content') || trigger.parentElement;
      let status = card.querySelector('[data-qp-card-status]');
      if (!status) {
        status = document.createElement('p');
        status.dataset.qpCardStatus = '';
        status.setAttribute('aria-live', 'polite');
        trigger.insertAdjacentElement('afterend', status);
      }
      status.setAttribute('role', error ? 'alert' : 'status');
      status.textContent = message;
      status.hidden = !message;
    }

    setMessage(form, message, error = false) {
      let status = form.querySelector('[data-qp-error], [data-qp-message]');
      if (!status) {
        status = document.createElement('p');
        status.dataset.qpMessage = '';
        status.setAttribute('aria-live', 'polite');
        (form.querySelector('[data-qp-actions]') || form).prepend(status);
      }
      status.setAttribute('role', error ? 'alert' : 'status');
      status.textContent = message;
      status.hidden = !message;
    }

    ownsHistory() {
      return !!this.historyKey && history.state?.maxlerQuickPurchase?.key === this.historyKey;
    }

    async open(trigger) {
      this.returnFocusRequest = null;
      this.trigger = trigger;
      this.loading = true;
      trigger.classList.add('btn--loading');
      trigger.setAttribute('aria-busy', 'true');
      this.setCardMessage(trigger, '');
      this.fetchController?.abort();
      this.fetchController = new AbortController();
      try {
        const url = new URL(trigger.dataset.productUrl, location.origin);
        if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error(this.text('error'));
        url.searchParams.set('section_id', 'maxler-quick-purchase-product');
        url.searchParams.delete('variant');
        const response = await fetch(url, {credentials: 'same-origin', signal: AbortSignal.any([this.fetchController.signal, AbortSignal.timeout(20000)]), headers: {'X-Requested-With': 'XMLHttpRequest'}});
        if (!response.ok) throw new Error(this.text('error'));
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const product = doc.querySelector('[data-qp-product]');
        if (!product || String(product.dataset.productId) !== String(trigger.dataset.productId)) throw new Error(this.text('error'));
        if (!this.isConnected) return;
        const imported = document.importNode(product, true);
        const idMap = new Map();
        imported.querySelectorAll('[id]').forEach(element => {
          const original = element.id;
          const unique = this.dataset.qpSection + '-' + original;
          idMap.set(original, unique);
          element.id = unique;
        });
        imported.querySelectorAll('[for], [aria-describedby], [aria-labelledby], [aria-controls], [form]').forEach(element => {
          for (const attribute of ['for', 'aria-describedby', 'aria-labelledby', 'aria-controls', 'form']) {
            if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map(value => idMap.get(value) || value).join(' '));
          }
        });
        this.formEvents?.abort();
        this.content.replaceChildren(imported);
        const form = this.content.querySelector('[data-qp-form]');
        if (!form || typeof window.theme?.Variants !== 'function') throw new Error(this.text('error'));
        this.titleElement.textContent = trigger.dataset.productTitle;
        this.details.href = trigger.dataset.productUrl;
        this.setupForm(form);
        this.historyKey = this.dataset.qpSection + ':' + Date.now().toString(36);
        history.pushState({...history.state, maxlerQuickPurchase: {key: this.historyKey}}, '', location.href);
        this.scrollStyles = {html: document.documentElement.style.overflow, body: document.body.style.overflow};
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        this.dialog.showModal();
        this.content.scrollTop = 0;
        this.querySelector('[data-qp-close]')?.focus({preventScroll: true});
      } catch (error) {
        if (error.name !== 'AbortError') this.setCardMessage(trigger, this.text('error'), true);
        if (this.ownsHistory()) await this.close();
      } finally {
        this.loading = false;
        trigger.classList.remove('btn--loading');
        trigger.removeAttribute('aria-busy');
      }
    }

    async close({consumeHistory = true, returnFocus = true} = {}) {
      this.flavourPickers?.forEach(picker => picker.close(false));
      const wasOpen = this.dialog?.open;
      if (wasOpen) this.returnFocusRequest = returnFocus && this.trigger ? {
        trigger: this.trigger, productId: this.trigger.dataset.productId, until: Date.now() + 2000
      } : null;
      if (wasOpen) this.dialog.close();
      if (this.scrollStyles) {
        document.documentElement.style.overflow = this.scrollStyles.html;
        document.body.style.overflow = this.scrollStyles.body;
        this.scrollStyles = null;
      }
      if (consumeHistory && this.ownsHistory()) {
        await new Promise(resolve => {
          const timeout = setTimeout(() => {
            if (this.ownsHistory()) {
              const state = {...history.state};
              delete state.maxlerQuickPurchase;
              history.replaceState(state, '', location.href);
            }
            this.historyClosing = null;
            resolve();
          }, 650);
          this.historyClosing = () => { clearTimeout(timeout); resolve(); };
          history.back();
        });
      }
      if (wasOpen && returnFocus && !MaxlerQuickPurchase.submitting) this.restoreCardFocus();
    }

    restoreCardFocus(afterReload = false) {
      const request = this.returnFocusRequest;
      if (!request) return;
      if (!this.dialog.open && Date.now() <= request.until
        && (document.activeElement === document.body || document.activeElement === request.trigger)) {
        // Motion may replace the grid on the history pop that closes this dialog.
        // Reuse its completion event; never alter filters or poll for new cards.
        const trigger = request.trigger.isConnected ? request.trigger : [...this.querySelectorAll('[data-qp-open]')]
          .find(button => button.dataset.productId === request.productId);
        if (trigger && !trigger.disabled) {
          this.trigger = trigger;
          trigger.focus({preventScroll: true});
        }
      }
      if (afterReload || Date.now() > request.until) this.returnFocusRequest = null;
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const elements = [...this.dialog.querySelectorAll('button, a[href], input, select, textarea, [tabindex]')].filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      if (!elements.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }
      const first = elements[0], last = elements.at(-1);
      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !this.dialog.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }

    isPurchasable(variant) {
      return !!variant?.available && (!variant.requires_selling_plan || variant.plans?.length > 0);
    }

    setupForm(form) {
      const data = form.closest('[data-qp-product]').querySelector('[data-qp-variants]');
      const variants = JSON.parse(data?.textContent || '[]');
      if (!Array.isArray(variants) || !variants.length) throw new Error(this.text('error'));
      // Preselect one real combination: available for purchase, or a visible sold-out
      // variant when the whole product is a manual demand request. This only initializes
      // the visible selector; it never adds anything to the cart automatically.
      const requestOnlyProduct = form.closest('[data-qp-product]').hasAttribute('data-qp-request-only');
      const eligibleDefault = variant => requestOnlyProduct ? !this.isPurchasable(variant) : this.isPurchasable(variant);
      const defaultId = form.closest('[data-qp-product]').dataset.qpDefaultVariant;
      const defaultVariant = variants.find(variant => String(variant.id) === defaultId && eligibleDefault(variant)) || variants.find(eligibleDefault);
      if (defaultVariant) {
        form.querySelectorAll('[data-qp-option]').forEach(select => {
          select.value = defaultVariant[select.dataset.index] || '';
        });
        form.elements.quantity.value = defaultVariant.quantity_min || defaultVariant.quantity_rule?.min || 1;
      }
      class ScopedVariants extends window.theme.Variants {
        _onSelectChange() {
          this.currentlySelectedValues = this._getCurrentOptions();
          this.currentVariant = this._getVariantFromOptions();
          this.container.dispatchEvent(new CustomEvent('variantChange', {detail: {variant: this.currentVariant}}));
        }
      }
      const engine = new ScopedVariants({container: form, variants, singleOptionSelector: '[data-qp-option]', originalSelectorId: '[data-qp-id]', enableHistoryState: false, dynamicVariantsEnabled: false});
      this.formEvents?.abort();
      this.formEvents = new AbortController();
      const state = {form, variants, engine, variant: null, submitting: false, notificationMode: requestOnlyProduct, flavourPickers: []};
      form.querySelectorAll('[data-qp-option]').forEach(select => {
        [...select.options].forEach((option, index) => {
          option.dataset.qpOrder = String(index);
          if (option.value) option.dataset.qpLabel ||= option.textContent;
        });
        if (select.dataset.qpKind === 'size') {
          const value = select.value;
          const amounts = [...select.options].filter(option => option.value).map(option => {
            const match = option.value.match(/(\d+(?:[.,]\d+)?)\s*(kg|mg|g|ml|l)\b/i);
            if (!match) return null;
            const unit = match[2].toLowerCase();
            return {option, family: ['ml', 'l'].includes(unit) ? 'volume' : 'mass',
              amount: Number(match[1].replace(',', '.')) * ({kg: 1000, mg: .001, g: 1, ml: 1, l: 1000})[unit]};
          });
          if (amounts.length && amounts.every(item => item && item.family === amounts[0].family)) {
            const sorted = amounts.sort((a, b) => a.amount - b.amount || Number(a.option.dataset.qpOrder) - Number(b.option.dataset.qpOrder));
            sorted.forEach(item => select.append(item.option));
            select.value = value;
          }
        }
        // Native option lists must stay stable while focused/open. Refresh their
        // partition only after focus leaves, or another dimension changes.
        select.addEventListener('blur', () => this.update(state));
      });
      const product = form.closest('[data-qp-product]');
      if (typeof window.MaxlerFlavourPicker === 'function') {
        state.flavourPickers = [...form.querySelectorAll('[data-qp-kind="flavour"]')]
          .filter(select => [...select.options].filter(option => option.value).length > 1)
          .map(select => new window.MaxlerFlavourPicker(select, {
            signal: this.formEvents.signal,
            labels: {
              available: product.dataset.qpFlavourAvailable,
              unavailable: product.dataset.qpFlavourUnavailable,
              empty: product.dataset.qpFlavourEmpty,
              hint: product.dataset.qpFlavourHint
            }
          }));
      }
      this.flavourPickers = state.flavourPickers;
      form.addEventListener('variantChange', () => this.update(state));
      form.addEventListener('change', event => {
        if (!event.target.matches('[data-qp-option]')) this.update(state);
      });
      form.addEventListener('input', event => {
        if (event.target.name === 'quantity') this.update(state);
      });
      form.addEventListener('submit', event => {
        event.preventDefault();
        if (MaxlerQuickPurchase.submitting || state.submitting) return;
        this.update(state);
        if (!this.isPurchasable(state.variant) || form.querySelector('[data-qp-submit]').disabled || !form.reportValidity()) return;
        const quantity = Number(form.elements.quantity.value);
        const plan = form.querySelector('[data-qp-plan]');
        this.add(form, form.querySelector('[data-qp-submit]'), {id: state.variant.id, quantity, ...(plan && !plan.disabled && plan.value !== 'one-time' ? {selling_plan: plan.value} : {})}, state);
      });
      this.update(state);
    }

    update(state) {
      const {form, variants, engine} = state;
      const selects = [...form.querySelectorAll('[data-qp-option]')];
      let selectedFixedOption = false;
      const demandMode = form.closest('[data-qp-product]').hasAttribute('data-qp-demand');
      selects.forEach((select, index) => {
        const possibleOptions = [];
        const fixedValues = new Set(variants.map(variant => variant[select.dataset.index]).filter(Boolean));
        const flavour = select.dataset.qpKind === 'flavour';
        const availability = new Map();
        for (const option of select.options) {
          if (!option.value) continue;
          const constraints = flavour ? selects.filter(other => other !== select) : selects.slice(0, index);
          const matching = variants.filter(variant => variant[select.dataset.index] === option.value
            && constraints.every(other => !other.value || variant[other.dataset.index] === other.value));
          const orderable = matching.some(variant => this.isPurchasable(variant));
          const possible = demandMode ? matching.length > 0 : orderable;
          availability.set(option.value, orderable);
          option.disabled = !possible;
          option.dataset.qpLabel ||= option.textContent;
          const product = form.closest('[data-qp-product]');
          const selectedSize = selects.some(other => other !== select && other.dataset.qpKind === 'size' && other.value);
          const noVariantStatus = flavour && selectedSize ? product.dataset.qpNoVariantSize : product.dataset.qpNoVariantCombination;
          const status = !matching.length ? noVariantStatus || this.text('unavailable') : !orderable ? this.text('unavailable') : '';
          option.dataset.qpAvailability = !matching.length ? 'missing' : orderable ? 'available' : 'unavailable';
          option.textContent = option.dataset.qpLabel + (!flavour && status ? ' — ' + status : '');
          if (possible) possibleOptions.push(option);
        }
        // Only a genuinely fixed product dimension can be filled automatically.
        // A contextual singleton must not refill a deliberately cleared choice.
        if (fixedValues.size === 1 && possibleOptions.length === 1 && select.value !== possibleOptions[0].value) {
          select.value = possibleOptions[0].value;
          selectedFixedOption = true;
        }
        if (select.selectedOptions[0]?.disabled) select.value = '';
        if (flavour && document.activeElement !== select) {
          const value = select.value;
          const ordered = [...select.options].sort((left, right) => {
            const rank = option => !option.value ? -1 : availability.get(option.value) ? 0 : 1;
            return rank(left) - rank(right) || Number(left.dataset.qpOrder) - Number(right.dataset.qpOrder);
          });
          if (ordered.some((option, index) => option !== select.options[index])) {
            ordered.forEach(option => select.append(option));
            select.value = value;
          }
        }
        let fixedOption = select.parentElement.querySelector('[data-qp-fixed-option]');
        if (fixedValues.size === 1 && possibleOptions.length === 1 && select.value === possibleOptions[0].value) {
          if (!fixedOption) {
            fixedOption = document.createElement('span');
            fixedOption.dataset.qpFixedOption = '';
            select.insertAdjacentElement('afterend', fixedOption);
          }
          fixedOption.textContent = possibleOptions[0].dataset.qpLabel;
          select.parentElement.dataset.qpSingleValue = '';
          select.hidden = true;
        } else {
          fixedOption?.remove();
          delete select.parentElement.dataset.qpSingleValue;
          select.hidden = false;
        }
      });
      if (selectedFixedOption) engine.currentlySelectedValues = engine._getCurrentOptions();
      const selected = selects.every(select => !!select.value);
      const variant = selected ? engine._getVariantFromOptions() : null;
      state.variant = variant;
      engine.currentVariant = variant;
      const purchasable = this.isPurchasable(variant);
      if (variant) state.notificationMode = !purchasable;
      const id = form.querySelector('[name="id"]');
      id.value = purchasable ? variant.id : '';
      id.disabled = !purchasable;
      const price = form.querySelector('[data-qp-price]');
      const image = form.querySelector('[data-qp-image]');
      const description = form.querySelector('[data-qp-variant-title]');
      if (variant) {
        if (price) price.textContent = variant.formatted_price || '';
        if (image && variant.image_url) {
          image.src = variant.image_url;
          image.removeAttribute('srcset');
          image.alt = this.trigger.dataset.productTitle + (variant.public_title || variant.title !== 'Default Title' ? ' — ' + (variant.public_title || variant.title) : '');
        }
        if (description) description.textContent = variant.public_title || (variant.title === 'Default Title' ? '' : variant.title);
      } else {
        if (description) description.textContent = '';
      }
      const planSelect = form.querySelector('[data-qp-plan]');
      let planValid = !variant?.requires_selling_plan;
      if (planSelect) {
        const previous = planSelect.value;
        const plans = variant?.plans || [];
        const signature = JSON.stringify([variant?.id, variant?.requires_selling_plan, plans]);
        if (planSelect.dataset.qpSignature !== signature) {
          planSelect.dataset.qpSignature = signature;
          planSelect.replaceChildren(new Option(this.text('choosePlan'), ''));
          if (variant && !variant.requires_selling_plan) planSelect.add(new Option(this.text('oneTime'), 'one-time'));
          plans.forEach(plan => planSelect.add(new Option(plan.name + (plan.formatted_price || plan.price ? ' — ' + (plan.formatted_price || plan.price) : ''), String(plan.id))));
          if (previous && [...planSelect.options].some(option => option.value === previous)) planSelect.value = previous;
          else if (variant && !variant.requires_selling_plan && planSelect.querySelector('option[value="one-time"]')) planSelect.value = 'one-time';
        }
        const active = !!variant && (plans.length > 0 || variant.requires_selling_plan);
        planSelect.disabled = !active;
        (planSelect.closest('[data-qp-plan-field]') || planSelect).hidden = !active;
        planValid = !active || planSelect.value === 'one-time' && !variant.requires_selling_plan || plans.some(plan => String(plan.id) === planSelect.value);
        const selectedPlan = plans.find(plan => String(plan.id) === planSelect.value);
        if (selectedPlan && price) price.textContent = selectedPlan.formatted_price || selectedPlan.price || variant.formatted_price;
      }
      const quantity = form.elements.quantity;
      const minimum = Number(variant?.quantity_min ?? variant?.quantity_rule?.min ?? 1);
      const increment = Number(variant?.quantity_increment ?? variant?.quantity_rule?.increment ?? 1);
      const maximum = variant?.quantity_max ?? variant?.quantity_rule?.max;
      quantity.min = minimum;
      quantity.step = increment;
      if (maximum != null && maximum !== '') quantity.max = maximum;
      else quantity.removeAttribute('max');
      const value = Number(quantity.value);
      const quantityValid = Number.isSafeInteger(value) && value >= minimum && (value - minimum) % increment === 0 && (maximum == null || maximum === '' || value <= Number(maximum));
      const rules = form.querySelector('[data-qp-quantity-rule]');
      if (rules) {
        rules.hidden = minimum === 1 && increment === 1 && maximum == null;
        let ruleText = this.text('quantityLimits') || this.text('quantityRule');
        if (maximum == null || maximum === '') ruleText = ruleText.split(' · ').filter(part => !part.includes('[max]')).join(' · ');
        rules.textContent = ruleText.replace('[min]', minimum).replace('[increment]', increment).replace('[max]', maximum ?? '');
      }
      const availability = form.querySelector('[data-qp-availability]');
      if (availability) availability.textContent = !selected ? this.text('choose') : !purchasable ? this.text('unavailable') : '';
      form.querySelector('[data-qp-submit]').disabled = !purchasable || !selected || !planValid || !quantityValid || state.submitting;
      const interest = form.closest('[data-qp-product]').querySelector('maxler-variant-interest');
      if (interest) {
        const product = form.closest('[data-qp-product]');
        const missing = selects.filter(select => !select.value);
        const flavourMissing = missing.some(select => select.dataset.qpKind === 'flavour');
        const sizeMissing = missing.some(select => select.dataset.qpKind === 'size');
        const helperKey = !missing.length ? 'qpInvalidCombination' : flavourMissing && sizeMissing ? 'qpMissingBoth'
          : flavourMissing ? 'qpMissingFlavour' : sizeMissing ? 'qpMissingSize' : 'qpMissingOptions';
        interest.updateVariant?.(variant ? {...variant, available: purchasable} : null, {
          keepVisible: state.notificationMode,
          helper: variant ? '' : product.dataset[helperKey] || this.text('choose')
        });
        const requestOnly = state.notificationMode;
        form.querySelector('[data-qp-actions]').hidden = requestOnly;
        form.querySelectorAll('[data-qp-purchase-fields] > div').forEach(field => {
          if (field.querySelector('[name="quantity"], [data-qp-plan]')) field.hidden = requestOnly || field.hasAttribute('data-qp-plan-field') && !variant?.plans?.length;
        });
      }
      state.flavourPickers.forEach(picker => picker.sync());
    }

    async directAdd(trigger) {
      this.setCardMessage(trigger, '');
      const form = document.createElement('form');
      form.hidden = true;
      const id = document.createElement('input');
      id.name = 'id';
      id.value = trigger.dataset.directVariant;
      form.append(id);
      trigger.insertAdjacentElement('afterend', form);
      try {
        await this.add(form, trigger, {id: trigger.dataset.directVariant, quantity: 1});
      } finally {
        form.remove();
      }
    }

    async add(form, button, item, state) {
      if (MaxlerQuickPurchase.submitting) return;
      MaxlerQuickPurchase.submitting = true;
      if (state) state.submitting = true;
      const triggers = [...this.querySelectorAll('[data-qp-open]')].map(trigger => ({trigger, disabled: trigger.disabled}));
      triggers.forEach(({trigger}) => trigger.disabled = true);
      button.disabled = true;
      button.classList.add('btn--loading');
      button.setAttribute('aria-busy', 'true');
      if (state) this.setMessage(form, '');
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(window.theme?.routes?.cartAdd || root + 'cart/add.js', location.origin);
        if (url.origin !== location.origin) throw new Error(this.text('error'));
        const response = await fetch(url, {method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(20000), headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body: JSON.stringify(item)});
        let product;
        try { product = await response.json(); } catch { throw new Error(this.text('error')); }
        if (!response.ok || product.status >= 400) {
          const error = new Error(this.text('error'));
          if (typeof product.description === 'string') error.shopifyMessage = product.description;
          throw error;
        }
        if (String(product.variant_id || product.id) !== String(item.id) || !Number.isSafeInteger(Number(product.quantity)) || Number(product.quantity) < item.quantity) throw new Error(this.text('error'));
        if (state) await this.close({returnFocus: false});
        else this.setCardMessage(button, this.text('added'));
        const event = new CustomEvent('ajaxProduct:added', {detail: {product, addToCartBtn: button}, bubbles: true});
        (form.isConnected ? form : document).dispatchEvent(event);
        if (window.theme?.settings?.cartType === 'page') location.assign(window.theme.routes.cartPage || root + 'cart');
      } catch (error) {
        const message = error.shopifyMessage || this.text('error');
        if (state && this.dialog?.open) this.setMessage(form, message, true);
        else this.setCardMessage(state ? this.trigger : button, message, true);
      } finally {
        MaxlerQuickPurchase.submitting = false;
        if (state) state.submitting = false;
        triggers.forEach(({trigger, disabled}) => trigger.disabled = disabled);
        button.classList.remove('btn--loading');
        button.removeAttribute('aria-busy');
        if (state) this.update(state);
        else button.disabled = false;
      }
    }
  }

  customElements.define('maxler-quick-purchase', MaxlerQuickPurchase);
})();
